import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { TocItemService } from "@viewer/core/toc-items/tocItem.service";
import { ViewerMessageService } from "@viewer/core/message/viewer.message.service";
import { SearchService } from "@viewer/core/search/search.service";
import { Store } from "@viewer/core/state/store";
import { reaction } from "mobx";
import { InspectionService } from "@viewer/core/toc-items/inspection.service";
import { BookmarkTreeService } from "@viewer/toc-items/bookmark-module/bookmark-list/bookmark-tree.service";
import { CommentService } from "@viewer/toc-items/comments-module/services/comment.service";
import { environment } from "@viewer-env/environment";
import { CheckDBScope, TocItemScope } from "@orion2/models/tocitem.models";
import { NoteService } from "@viewer/core/toc-items/note.service";

export enum SynchroIcon {
  ERROR = "cloud-alert",
  OFF = "cloud-off-outline",
  OK = "cloud-check-outline",
  PENDING = "autorenew"
}

@Injectable()
export class SynchroService {
  public statusIcon = SynchroIcon.OK;

  constructor(
    private translate: TranslateService,
    private messageService: ViewerMessageService,
    private tocItemService: TocItemService,
    private searchService: SearchService,
    private store: Store,
    private inspectionService: InspectionService,
    private bookmarkTreeService: BookmarkTreeService,
    private commentService: CommentService,
    private noteService: NoteService
  ) {
    window.addEventListener("startSynchro", this.synchronize.bind(this));

    reaction(
      () => [this.store.userAutoSynchro, this.store.isLoggedIn, this.store.syncSettings],
      () => {
        if (!this.store.isLoggedIn) {
          this.statusIcon = SynchroIcon.OFF;
          return;
        }
        if (this.statusIcon === SynchroIcon.OK || this.statusIcon === SynchroIcon.OFF) {
          // SPEC: Admin can disable the synchro inside the admin interface
          this.statusIcon =
            this.store.userAutoSynchro && this.store.syncSettings.enable
              ? SynchroIcon.OK
              : SynchroIcon.OFF;
        }
      },
      { fireImmediately: true }
    );
  }

  /**
   * Entrypoint to trigger a synchronization.
   */
  public synchronize(): Promise<void> {
    if (TocItemService.syncPending) {
      return Promise.resolve();
    }

    // SPEC: To avoid NG0100: Expression has changed after it was checked
    setTimeout(() => {
      this.statusIcon = SynchroIcon.PENDING;
      TocItemService.syncPending = true;
    });

    return this.checkUserDbs()
      .then(() => this.tocItemService.synchronize())
      .then(() => {
        TocItemService.shouldResetCache.next();
        this.inspectionService.listShouldUpdate.next(true);
        this.bookmarkTreeService.getTree();
        this.searchService.updateSearch();
        this.noteService.refresh();
        if (this.store.isGov) {
          this.commentService.refresh();
        }
        setTimeout(() => {
          this.statusIcon =
            this.store.userAutoSynchro && this.store.syncSettings.enable
              ? SynchroIcon.OK
              : SynchroIcon.OFF;
        });
      })
      .catch((err: Error) => {
        console.error("Error during synchronization : ", err);
        this.statusIcon = SynchroIcon.ERROR;
        this.messageService.error(this.translate.instant("synchronize.error"));
      })
      .finally(() => {
        TocItemService.syncPending = false;
        this.store.shouldSyncScopes = new Set([TocItemScope.APP, TocItemScope.USER]);
      });
  }

  /**
   * Check if the required user/corp databases exist, and create them if they don't.
   */
  private async checkUserDbs(): Promise<boolean> {
    const scope = this.getDbToCheck();
    if (!scope) {
      return Promise.resolve(false);
    }

    // SPEC: If pub is offline, we still need to check remote user DBs
    return environment.authService
      .checkUserDbs(this.store.pubInfo.occurrenceCode, scope)
      .then((ok: boolean) => {
        if (!ok) {
          const message = this.translate.instant("userDbs.check.error", {
            pubName: this.store.pubInfo.verbatimText
          });
          console.error(message);
          this.messageService.error(message);
          return false;
        }
        return true;
      });
  }

  /**
   * Determines which database should be checked based on shouldSyncScopes array.
   * - If neither 'private' nor 'corporate' is set, returns `undefined`.
   * - If both are set, returns 'all'.
   * - If only one is set, returns 'private' or 'corporate' accordingly.
   * @returns {CheckDBScope} The scope of the database to check:
   */
  private getDbToCheck(): CheckDBScope {
    const userCheck = this.store.shouldSyncScopes.has(TocItemScope.PRIVATE);
    const corpCheck = this.store.shouldSyncScopes.has(TocItemScope.CORPORATE);

    if (!userCheck && !corpCheck) {
      return;
    }
    if (userCheck && corpCheck) {
      return CheckDBScope.ALL;
    }
    return userCheck ? CheckDBScope.PRIVATE : CheckDBScope.CORPORATE;
  }
}
